import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { EvaluatedRoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import { RoofTruncationType, type RoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type {
  EvaluatedRoofEdgeOverridesTable,
  EvaluatedRoofSideOverridesTable,
  IsRoofEdgeAlignedToSideOptions,
  RoofEdgeOverridesTable,
  RoofHeelHeightOffsets,
  RoofSideOverridesTable,
  RoofSlope,
  RoofTruncationOptions,
  SlopeOverlapRange,
} from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import {
  roofSlopeNew,
  MULTIGABLE_DEFAULT_SLOPE,
  MULTIGABLE_INFINITE_SLOPE,
  evaluatedRoofSlopeOptionsNew,
} from "doc/utils/roofTools";
import { DIST_EPSILON } from "geom/core";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { IntersectionKind, intersectLine2Ray2 } from "geom/intersectRay2";
import { line2Collinear, line2Eval, line2Tangent } from "geom/line2";
import { polyLine2Edge, polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { ascendingSortCompare } from "geom/utils";
import { getHeelHeightOffset, NULL_HEEL_HEIGHT_OFFSETS } from "./roofHeelHeightOffsets";
import { DEFAULT_ROOF_TRUNCATION_OPTIONS } from "./roofTruncationDefaults";

export function flattenSlopes(slopes: RoofSlope[]): RoofSlope[] {
  const ret: RoofSlope[] = [];
  const baseToPeak = getBaseToPeak(slopes);
  const slope = baseToPeak / getExtensionAtHeightFromBase(slopes, baseToPeak);
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { startHeight, endHeight } = slopes[i];
    const newStartDist = startHeight / slope;
    const newEndDist = endHeight / slope;
    ret.push(roofSlopeNew(slope, newEndDist - newStartDist, newStartDist, newEndDist, startHeight, endHeight));
  }

  return ret;
}

interface GetSubRoofEdgeOverrideSlopeOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly side: RoofSide;
  readonly roofEdgeOverrides?: RoofEdgeOverridesTable;
}

interface GetSubRoofEdgeOverrideSlopeResult {
  readonly slope?: number;
  readonly slopeOptions?: RoofSlopeOptions[];
}

function getSubRoofEdgeOverrideSlope({
  boundingPerimeter,
  exactPerimeter,
  startEdge,
  side,
  roofEdgeOverrides = {},
}: GetSubRoofEdgeOverrideSlopeOptions): GetSubRoofEdgeOverrideSlopeResult {
  let slope: number | undefined;
  let slopeOptions: RoofSlopeOptions[] | undefined;

  for (let i = 0, l = polyLine2EdgeCount(exactPerimeter); i < l; i++) {
    const { slope: edgeSlope = Number.POSITIVE_INFINITY } = roofEdgeOverrides[i] ?? {};

    if (edgeSlope === Number.POSITIVE_INFINITY) {
      continue;
    }

    if (
      !isRoofEdgeAlignedToSide({
        boundingPerimeter,
        exactPerimeter,
        startEdge,
        side,
        edgeIndex: i,
      })
    ) {
      continue;
    }

    assertIsNotFormula(slope);
    assertIsNotFormula(edgeSlope);

    slope = Math.min(slope ?? edgeSlope, edgeSlope);
    slopeOptions = [];
  }

  return {
    slope,
    slopeOptions,
  };
}

interface GetSubRoofSlopesOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly roofSettings: RoofSettings | DormerSettings;
  readonly roofSideOverrides?: EvaluatedRoofSideOverridesTable;
  readonly roofEdgeOverrides?: EvaluatedRoofEdgeOverridesTable;
  readonly defaultSlope?: number;
}

export function getSubRoofSlopes({
  exactPerimeter,
  boundingPerimeter,
  startEdge,
  roofSettings,
  roofSideOverrides = {},
  roofEdgeOverrides = {},
  defaultSlope = MULTIGABLE_DEFAULT_SLOPE,
}: GetSubRoofSlopesOptions): EvaluatedRoofSlopeOptions[] {
  const { slope, slopes } = roofSettings;
  const { slope: slopeOverride, slopeOptions: slopeOptionsOverride } = roofSideOverrides[RoofSide.LEFT] ?? {};
  const { slope: slopeEdgeOverride, slopeOptions: slopeOptionsEdgeOverride } = getSubRoofEdgeOverrideSlope({
    exactPerimeter,
    boundingPerimeter,
    startEdge,
    roofEdgeOverrides,
    side: RoofSide.LEFT,
  });

  const finalSlope = slopeEdgeOverride ?? slopeOverride ?? slope;
  const finalSlopeOptions =
    slopeOptionsEdgeOverride ?? slopeOptionsOverride ?? (slopeOverride === undefined ? slopes : undefined);

  assertIsNotFormula(finalSlope);

  if (finalSlopeOptions && finalSlopeOptions.length > 0) {
    assertIsEvaluatedRoofSlopeOptions(finalSlopeOptions);
    return finalSlopeOptions;
  } else {
    return [evaluatedRoofSlopeOptionsNew(finalSlope || defaultSlope, -1.0)];
  }
}

export function assertIsEvaluatedRoofSlopeOptions(
  slopes: RoofSlopeOptions[] | undefined,
): asserts slopes is EvaluatedRoofSlopeOptions[] | undefined {
  if (!slopes) {
    return;
  }

  slopes.forEach(assertIsNotFormula);
}

interface GetSubRoofRightSlopesOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly roofSettings: RoofSettings;
  readonly roofSideOverrides?: RoofSideOverridesTable;
  readonly roofEdgeOverrides?: RoofEdgeOverridesTable;
  readonly defaultSlope?: number;
}

export function getSubRoofRightSlopes({
  boundingPerimeter,
  exactPerimeter,
  startEdge,
  roofSettings,
  roofSideOverrides = {},
  roofEdgeOverrides = {},
  defaultSlope = MULTIGABLE_DEFAULT_SLOPE,
}: GetSubRoofRightSlopesOptions): EvaluatedRoofSlopeOptions[] {
  const { rightSlope, rightSlopes } = roofSettings;
  const { slope: rightSlopeOverride, slopeOptions: rightSlopeOptionsOverride } =
    roofSideOverrides[RoofSide.RIGHT] ?? {};
  const { slope: slopeEdgeOverride, slopeOptions: slopeOptionsEdgeOverride } = getSubRoofEdgeOverrideSlope({
    exactPerimeter,
    boundingPerimeter,
    startEdge,
    roofEdgeOverrides,
    side: RoofSide.RIGHT,
  });

  const finalSlope = slopeEdgeOverride ?? rightSlopeOverride ?? rightSlope;

  const finalSlopeOptions =
    slopeOptionsEdgeOverride ??
    rightSlopeOptionsOverride ??
    (rightSlopeOverride === undefined ? rightSlopes : undefined);

  if (finalSlopeOptions && finalSlopeOptions.length > 0) {
    assertIsEvaluatedRoofSlopeOptions(finalSlopeOptions);
    return finalSlopeOptions;
  } else if (finalSlope !== undefined) {
    assertIsNotFormula(finalSlope);
    return [evaluatedRoofSlopeOptionsNew(finalSlope || defaultSlope, -1.0)];
  } else {
    return getSubRoofSlopes({ exactPerimeter, boundingPerimeter, startEdge, roofSettings, defaultSlope });
  }
}

interface BuildSlopesOptions {
  readonly heelHeightOffsets?: RoofHeelHeightOffsets;
  readonly slopes: EvaluatedRoofSlopeOptions[];
  readonly oppSideSlopes?: EvaluatedRoofSlopeOptions[];
  readonly halfSpan: number;
  readonly oppHalfSpan?: number;
  readonly startNeighborSlopes?: RoofSlope[];
  readonly endNeighborSlopes?: RoofSlope[];
  readonly startTruncation?: RoofTruncationOptions;
  readonly endTruncation?: RoofTruncationOptions;
  readonly flipTruncationOrder?: boolean;
  readonly startRelativeHeight?: number;
  readonly endRelativeHeight?: number;
  readonly roofOverhang?: number;
  readonly oppRoofOverhang?: number;
  readonly heightOffset?: number;
  readonly oppHeightOffset?: number;
  readonly startOverlap?: SlopeOverlapRange;
  readonly endOverlap?: SlopeOverlapRange;
}

export function buildSlopes({
  heelHeightOffsets = NULL_HEEL_HEIGHT_OFFSETS,
  slopes,
  oppSideSlopes = [],
  halfSpan,
  oppHalfSpan = halfSpan,
  startNeighborSlopes = [],
  endNeighborSlopes = [],
  startTruncation = DEFAULT_ROOF_TRUNCATION_OPTIONS,
  endTruncation = DEFAULT_ROOF_TRUNCATION_OPTIONS,
  flipTruncationOrder = false,
  startRelativeHeight = 0,
  endRelativeHeight = 0,
  roofOverhang = 0,
  oppRoofOverhang = 0,
  heightOffset = 0,
  oppHeightOffset = 0,
  startOverlap,
  endOverlap,
}: BuildSlopesOptions): RoofSlope[] {
  let res: RoofSlope[] = [];

  const tmpOppSideSlopes =
    slopes !== oppSideSlopes && oppSideSlopes.length > 0
      ? buildSlopes({
          heelHeightOffsets,
          slopes: oppSideSlopes,
          halfSpan: oppHalfSpan,
          roofOverhang: oppRoofOverhang,
          heightOffset: oppHeightOffset,
        })
      : [];

  // get the extra splits from its neighbor
  let extraSplitHeights: number[] = startNeighborSlopes
    .filter((x) => x.endHeight > heightOffset)
    .map((x) => x.endHeight - Math.abs(startRelativeHeight));
  extraSplitHeights.push(
    ...endNeighborSlopes
      .filter((x) => x.endHeight > heightOffset)
      .map((x) => x.endHeight - Math.abs(endRelativeHeight)),
  );

  // overhang splits
  if (roofOverhang > DIST_EPSILON) {
    if (slopes.length > 0) {
      extraSplitHeights.push(roofOverhang * slopes[0].slope + heightOffset);
    }
  }

  if (oppRoofOverhang > DIST_EPSILON && Math.abs(oppRoofOverhang - roofOverhang) > DIST_EPSILON) {
    if (oppSideSlopes.length > 0) {
      const oppRoofOverhangHeight = oppRoofOverhang * oppSideSlopes[0].slope + oppHeightOffset;
      if (oppRoofOverhangHeight - heightOffset > DIST_EPSILON) {
        extraSplitHeights.push(oppRoofOverhangHeight);
      }
    }
  }

  // opp. side extra splits
  if (tmpOppSideSlopes.length > 0) {
    if (oppHeightOffset > DIST_EPSILON) {
      extraSplitHeights.push(oppHeightOffset);
    }

    extraSplitHeights.push(...tmpOppSideSlopes.filter((x) => x.endHeight > heightOffset).map((x) => x.endHeight));
  }

  // sort the splits
  extraSplitHeights.sort(ascendingSortCompare);

  // remove duplicates and splits that are below the base height for this roof side
  extraSplitHeights = extraSplitHeights.filter(
    (x, i, elems) => x >= heightOffset && (i === 0 || Math.abs(x - elems[i - 1]) > DIST_EPSILON),
  );
  const extraSplitCount = extraSplitHeights.length;

  // generate the slope segments
  let availableDist = halfSpan;
  let startDist = 0.0;
  let startHeight = heightOffset;
  let currExtraSplitId = 0;
  let currExtraSplitHeight =
    currExtraSplitId < extraSplitCount ? extraSplitHeights[currExtraSplitId] : Number.MAX_VALUE;
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { slope, horizontalDist } = slopes[i];
    let newHorizontalDist = horizontalDist;

    if (i === l - 1) {
      newHorizontalDist = availableDist;
    } else if (newHorizontalDist === undefined || newHorizontalDist < 0.0) {
      newHorizontalDist = availableDist * 0.5;
    } else if (i === 0) {
      newHorizontalDist = Math.min(newHorizontalDist + roofOverhang, availableDist);
    } else {
      newHorizontalDist = Math.min(newHorizontalDist, availableDist);
    }

    availableDist -= newHorizontalDist;

    let availableHeight = newHorizontalDist * slope;

    const endDist = startDist + newHorizontalDist;
    const endHeight = startHeight + availableHeight;

    if (newHorizontalDist > DIST_EPSILON) {
      while (endHeight > currExtraSplitHeight) {
        const lerpFactor = (currExtraSplitHeight - startHeight) / availableHeight;
        const segmentLength = newHorizontalDist * lerpFactor;

        if (segmentLength > DIST_EPSILON) {
          res.push(
            roofSlopeNew(
              slope,
              segmentLength,
              startDist,
              startDist + segmentLength,
              startHeight,
              currExtraSplitHeight,
              !startOverlap,
              !endOverlap,
            ),
          );
        }

        availableHeight -= currExtraSplitHeight - startHeight;
        newHorizontalDist -= segmentLength;
        startHeight = currExtraSplitHeight;
        startDist += segmentLength;

        currExtraSplitId++;
        currExtraSplitHeight =
          currExtraSplitId < extraSplitCount ? extraSplitHeights[currExtraSplitId] : Number.MAX_VALUE;
      }

      const segmentLength = endDist - startDist;
      if (segmentLength > DIST_EPSILON) {
        res.push(
          roofSlopeNew(slope, segmentLength, startDist, endDist, startHeight, endHeight, !startOverlap, !endOverlap),
        );
      }
    }

    startDist = endDist;
    startHeight = endHeight;
  }

  const baseToPeak = getBaseToPeak(res);

  const addSplitAtHeight = (splitHeight: number) => {
    if (splitHeight > DIST_EPSILON && splitHeight < baseToPeak - DIST_EPSILON) {
      res = insertSlopeSplit(res, splitHeight);
    }
  };

  const addTruncationSplit = (truncationOptions: RoofTruncationOptions, isEndSide: boolean) => {
    const truncationOffset = getHeelHeightOffset(
      heelHeightOffsets,
      isEndSide !== flipTruncationOrder ? RoofSide.END : RoofSide.START,
    );
    const truncationHeight = getTruncatedHeight(truncationOptions, baseToPeak, truncationOffset);

    addSplitAtHeight(truncationHeight);
  };

  addTruncationSplit(startTruncation, false);
  addTruncationSplit(endTruncation, true);

  if (startOverlap) {
    res = insertOverlapSplit(res, false, startOverlap);
  }

  if (endOverlap) {
    res = insertOverlapSplit(res, true, endOverlap);
  }

  return res;
}

function insertSlopeSplit(slopes: RoofSlope[], heightFromBase: number): RoofSlope[] {
  const ret: RoofSlope[] = [];

  for (let i = 0, l = slopes.length; i < l; i++) {
    const currSlope = slopes[i];
    const { horizontalDist, startDist, endDist, startHeight, endHeight, slope } = currSlope;

    if (heightFromBase > startHeight && heightFromBase < endHeight) {
      const heightDiff = heightFromBase - startHeight;
      const midDist = startDist + heightDiff / slope;

      ret.push(roofSlopeNew(slope, midDist - startDist, startDist, midDist, startHeight, heightFromBase));
      ret.push(roofSlopeNew(slope, endDist - midDist, midDist, endDist, heightFromBase, endHeight));
    } else {
      ret.push(roofSlopeNew(slope, horizontalDist, startDist, endDist, startHeight, endHeight));
    }
  }

  return ret;
}

function insertOverlapSplit(slopes: RoofSlope[], endSide: boolean, overlapRange: SlopeOverlapRange): RoofSlope[] {
  const ret: RoofSlope[] = [];

  const h1 = getHeightAtDistFromEdge(slopes, overlapRange.t1);
  const h2 = getHeightAtDistFromEdge(slopes, overlapRange.t2);

  let isInRange = false;
  for (let i = 0, l = slopes.length; i < l; i++) {
    const currSlope = slopes[i];
    const {
      horizontalDist,
      startDist,
      endDist,
      startHeight,
      endHeight,
      slope,
      hasStartOverlapping,
      hasEndOverlapping,
    } = currSlope;

    let wasAdded = false;
    let nextStartOverlapping = false;
    let nextEndOverlapping = false;
    let prevHeight = startHeight;
    let prevDist = startDist;

    if (h1 > prevHeight + DIST_EPSILON && h1 < endHeight - DIST_EPSILON) {
      const heightDiff = h1 - prevHeight;

      const midHeight = h1;
      const midDist = prevDist + heightDiff / slope;

      const prevStartOverlapping = endSide ? hasStartOverlapping : false;
      const prevEndOverlapping = endSide ? false : hasEndOverlapping;

      nextStartOverlapping = endSide ? hasStartOverlapping : true;
      nextEndOverlapping = endSide ? true : hasEndOverlapping;

      ret.push(
        roofSlopeNew(
          slope,
          midDist - prevDist,
          prevDist,
          midDist,
          prevHeight,
          h1,
          prevStartOverlapping,
          prevEndOverlapping,
        ),
      );

      prevHeight = midHeight;
      prevDist = midDist;

      wasAdded = true;
    }

    if (h1 > prevHeight - DIST_EPSILON) {
      isInRange = true;
    }

    if (h2 > prevHeight + DIST_EPSILON && h2 < endHeight - DIST_EPSILON) {
      const heightDiff = h2 - prevHeight;

      const midHeight = h2;
      const midDist = prevDist + heightDiff / slope;

      const prevStartOverlapping = endSide ? hasStartOverlapping : true;
      const prevEndOverlapping = endSide ? true : hasEndOverlapping;

      nextStartOverlapping = endSide ? hasStartOverlapping : false;
      nextEndOverlapping = endSide ? false : hasEndOverlapping;

      ret.push(
        roofSlopeNew(
          slope,
          midDist - prevDist,
          prevDist,
          midDist,
          prevHeight,
          h2,
          prevStartOverlapping,
          prevEndOverlapping,
        ),
      );

      prevHeight = midHeight;
      prevDist = midDist;

      wasAdded = true;
    }

    if (h2 > endHeight + DIST_EPSILON) {
      isInRange = false;
    }

    if (wasAdded) {
      if (endDist - prevDist > DIST_EPSILON) {
        ret.push(
          roofSlopeNew(
            slope,
            endDist - prevDist,
            prevDist,
            endDist,
            prevHeight,
            endHeight,
            nextStartOverlapping,
            nextEndOverlapping,
          ),
        );
      }
    } else {
      ret.push(
        roofSlopeNew(
          slope,
          horizontalDist,
          startDist,
          endDist,
          startHeight,
          endHeight,
          endSide || !isInRange ? hasStartOverlapping : false,
          !endSide || !isInRange ? hasEndOverlapping : false,
        ),
      );
    }
  }

  return ret;
}

export function getBaseToPeak(slopes: RoofSlope[]): number {
  return getLastSlope(slopes).endHeight;
}

export function getExtensionAtHeightFromBase(slopes: RoofSlope[], heightFromBase: number): number {
  let extension = 0.0;
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { startDist, endDist, startHeight, endHeight, slope } = slopes[i];

    if (heightFromBase >= endHeight) {
      extension += endDist - startDist;
    } else if (heightFromBase >= startHeight) {
      extension += (heightFromBase - startHeight) / slope;
      return extension;
    }
  }

  return extension;
}

export function getExtensions(
  slopes: RoofSlope[],
  neighborSlopes: RoofSlope[],
  allowPopUp: boolean = false,
  relativeDist: number = 0.0,
  relativeHeight: number = 0.0,
): number[] {
  const res: number[] = [];

  const baseToPeak = getBaseToPeak(slopes) + relativeHeight;
  const neighborBaseToPeak = getBaseToPeak(neighborSlopes);
  const allowExtension = allowPopUp || baseToPeak <= neighborBaseToPeak + 1;

  for (let i = 0, l = slopes.length; i < l; i++) {
    const endHeight = slopes[i].endHeight + relativeHeight;

    const extension = allowExtension ? getExtensionAtHeightFromBase(neighborSlopes, endHeight) - relativeDist : 0;

    res.push(Math.max(extension, 0));
  }

  return res;
}

export function getSideExtension(
  slopes: RoofSlope[],
  neighborSlopes: RoofSlope[],
  relativeDist: number = 0.0,
  relativeHeight: number = 0.0,
): number {
  const lastSlope = getLastSlope(slopes).slope;
  const oX = relativeDist;
  const oY = relativeHeight + getBaseToPeak(slopes);
  const [dirX, dirY] = line2Tangent(0, 0, 1, lastSlope);

  for (let i = 0, l = neighborSlopes.length; i < l; i++) {
    const { startDist, endDist, startHeight, endHeight } = neighborSlopes[i];

    if (oY > endHeight) {
      continue;
    }

    const intersection = intersectLine2Ray2(startDist, startHeight, endDist, endHeight, oX, oY, dirX, dirY);

    if (!intersection) {
      continue;
    }

    let intersectionT = 0;
    if (intersection.kind === IntersectionKind.TRANSVERSE) {
      intersectionT = intersection.lineT;
    }

    const [pX, pY] = line2Eval(startDist, startHeight, endDist, endHeight, intersectionT, false);

    if (pY > endHeight || pY < startHeight) {
      continue;
    }

    return Math.max(pX - oX, 0);
  }

  return 0;
}

export function isValidExtension(extensions: number[]) {
  return extensions && extensions.length > 0 && extensions[extensions.length - 1] > DIST_EPSILON;
}

export function getPeakExtension(extensions: number[]) {
  const l = extensions.length;
  return l > 0 ? extensions[l - 1] : 0.0;
}

export function getSideSpanAtHeightFromBase(sideSlopes: RoofSlope[], sideSpan: number, heightFromBase: number): number {
  let availableSpan = sideSpan;
  for (let i = 0, l = sideSlopes.length; i < l; i++) {
    const { slope, startHeight, endHeight, horizontalDist } = sideSlopes[i];

    if (heightFromBase < startHeight) {
      return availableSpan;
    }

    if (heightFromBase >= startHeight && heightFromBase <= endHeight) {
      return availableSpan - (heightFromBase - startHeight) / slope;
    }

    availableSpan -= horizontalDist;
  }

  return heightFromBase <= 0 ? sideSpan : 0;
}

export function getSlopeAtHeightFromBase(slopes: RoofSlope[], heightFromBase: number): number {
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { slope, startHeight, endHeight } = slopes[i];

    if (heightFromBase >= startHeight && heightFromBase <= endHeight) {
      return slope;
    }
  }

  return getLastSlope(slopes).slope;
}

export function getHalfSpan(slopes: EvaluatedRoofSlopeOptions[], baseToPeak: number, roofOverhang: number): number {
  let halfSpan = 0;
  let availableHeight = baseToPeak;
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { slope, horizontalDist } = slopes[i];

    let heightInc = 0;
    if (i === l - 1 || horizontalDist === undefined || horizontalDist < 0) {
      heightInc = availableHeight;
    } else if (i === 0) {
      heightInc = Math.min((horizontalDist + roofOverhang) * slope, availableHeight);
    } else {
      heightInc = Math.min(horizontalDist * slope, availableHeight);
    }

    halfSpan += heightInc / slope;
    availableHeight -= heightInc;

    if (availableHeight < DIST_EPSILON) {
      break;
    }
  }

  return halfSpan;
}

interface GetLeftSpanOptions {
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly leftSideSlopeOptions: EvaluatedRoofSlopeOptions[];
  readonly rightSideSlopeOptions: EvaluatedRoofSlopeOptions[];
  readonly span: number;
  readonly roofOverhang: number;
}

export function getLeftSpan({
  leftSideSlopeOptions,
  rightSideSlopeOptions,
  span,
  heelHeightOffsets,
  roofOverhang,
}: GetLeftSpanOptions): number {
  const leftSideSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: leftSideSlopeOptions,
    halfSpan: span,
    roofOverhang,
  });

  const rightSideSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: rightSideSlopeOptions,
    halfSpan: span,
    roofOverhang,
  });

  let leftI = 0;
  let rightI = 0;
  const leftCount = leftSideSlopes.length;
  const rightCount = rightSideSlopes.length;
  const maxLeftI = leftCount - 1;
  const maxRightI = rightCount - 1;

  const heelHeightDiff = heelHeightOffsets.rightOffset - heelHeightOffsets.leftOffset;

  while (leftI < leftCount || rightI < rightCount) {
    const { startDist: ax, endDist: bx, startHeight: ay, endHeight: by } = leftSideSlopes[Math.min(leftI, maxLeftI)];

    let { startDist: cx, endDist: dx, startHeight: cy, endHeight: dy } = rightSideSlopes[Math.min(rightI, maxRightI)];

    cx = span - cx;
    dx = span - dx;
    cy += heelHeightDiff;
    dy += heelHeightDiff;

    const intersection = intersectLine2Line2(ax, ay, bx, by, cx, cy, dx, dy);
    if (intersection && intersection instanceof CurveCurvePointIntersection) {
      return intersection.x;
    }

    if (dy < by && rightI < maxRightI) {
      rightI++;
    } else if (leftI < maxLeftI) {
      leftI++;
    } else {
      break;
    }
  }

  return span * 0.5;
}

export function getHeightAtDistFromEdge(slopes: RoofSlope[], dist: number): number {
  let lastHeight = 0;
  for (let i = 0, l = slopes.length; i < l; i++) {
    const { startDist, endDist, startHeight, endHeight, slope } = slopes[i];

    if (dist >= startDist && dist <= endDist) {
      return startHeight + (dist - startDist) * slope;
    }

    lastHeight = endHeight;
  }

  return lastHeight;
}

export function getFirstSlope(slopes: RoofSlope[]): RoofSlope {
  return slopes.length > 0 ? slopes[0] : MULTIGABLE_INFINITE_SLOPE;
}

export function getLastSlope(slopes: RoofSlope[]): RoofSlope {
  const l = slopes.length;
  return slopes.length > 0 ? slopes[l - 1] : MULTIGABLE_INFINITE_SLOPE;
}

export function getTruncatedHeight(
  truncationOptions: RoofTruncationOptions,
  baseToPeak: number,
  truncationOffset: number,
): number {
  let truncatedHeight = 0;
  switch (truncationOptions.type) {
    case RoofTruncationType.NONE: {
      truncatedHeight = baseToPeak;
      break;
    }
    case RoofTruncationType.HIP: {
      truncatedHeight = truncationOffset;
      break;
    }
    default: {
      if (truncationOptions.normalizedHeight) {
        truncatedHeight =
          Math.min(Math.max(truncationOptions.heightFactor, 0), 1) * Math.max(baseToPeak - truncationOffset, 0) +
          truncationOffset;
      } else {
        truncatedHeight =
          Math.max(Math.min(truncationOptions.heightFactor - truncationOffset, baseToPeak), 0) + truncationOffset;
      }
    }
  }

  return truncatedHeight;
}

export function isRoofEdgeAlignedToSide({
  boundingPerimeter,
  exactPerimeter,
  startEdge,
  side,
  edgeIndex,
}: IsRoofEdgeAlignedToSideOptions): boolean {
  const basicEdgeCount = polyLine2EdgeCount(boundingPerimeter);

  let refEdge: PolyLine2 | undefined;
  switch (side) {
    case RoofSide.START:
      refEdge = polyLine2Edge(boundingPerimeter, startEdge);
      break;
    case RoofSide.END:
      refEdge = polyLine2Edge(boundingPerimeter, (startEdge + 2) % basicEdgeCount);
      break;
    case RoofSide.LEFT:
      refEdge = polyLine2Edge(boundingPerimeter, (startEdge + 3) % basicEdgeCount);
      break;
    case RoofSide.RIGHT:
      refEdge = polyLine2Edge(boundingPerimeter, (startEdge + 1) % basicEdgeCount);
      break;
    case RoofSide.INVALID:
      break;
    default:
      assertUnreachable(side);
  }

  if (!refEdge) {
    return false;
  }

  const edge = polyLine2Edge(exactPerimeter, edgeIndex);

  return line2Collinear(edge[0], edge[1], edge[2], edge[3], refEdge[0], refEdge[1], refEdge[2], refEdge[3]);
}
